import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Typography,
  IconButton,
  Tooltip,
  Collapse,
  Alert,
  CircularProgress,
  Divider,
  Chip,
  Fab,
  Button
} from '@mui/material';
import {
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  ExpandLess,
  ExpandMore,
  Dashboard as DashboardIcon,
  Menu as MenuIcon,
  BarChart as ChartIcon,
  CheckCircle as CheckCircleIcon
} from '@mui/icons-material';
import { dashboardService, GuardrailApp, DashboardCategory } from '../services/dashboardService';

interface DashboardNavigationProps {
  clientId?: string;
  workflowId?: string;
  clientData?: any;
  iframeStyle?: React.CSSProperties;
  isMaximized?: boolean;
  onSelectedDashboardChange?: (dashboard: GuardrailApp | null) => void;
  updatedConversationId?: string;
}

const DRAWER_WIDTH = 280;
const COLLAPSED_DRAWER_WIDTH = 60;
const MOBILE_BREAKPOINT = 768;

const DashboardNavigation: React.FC<DashboardNavigationProps> = ({
  clientId,
  workflowId,
  clientData,
  iframeStyle,
  isMaximized = false,
  onSelectedDashboardChange,
  updatedConversationId
}) => {
  const [isDrawerOpen, setIsDrawerOpen] = useState(true);
  const [isMobile, setIsMobile] = useState(window.innerWidth < MOBILE_BREAKPOINT);
  const [categories, setCategories] = useState<DashboardCategory[]>([]);
  const [selectedDashboard, setSelectedDashboard] = useState<GuardrailApp | null>(null);
  const [dashboardLoading, setDashboardLoading] = useState(false);
  const [iframeLoading, setIframeLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [retryCount, setRetryCount] = useState(0);
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());
  const iframeRef = useRef<HTMLIFrameElement>(null);

  // Load dashboards on component mount and retry
  useEffect(() => {
    const loadDashboards = async () => {
      try {
        setLoading(true);
        setError('');
        
        console.log('Loading dashboards for clientId:', clientId, 'workflowId:', workflowId);
        const dashboardCategories = await dashboardService.getDashboardCategories(clientId, workflowId);
        console.log('Loaded dashboard categories:', dashboardCategories);
        setCategories(dashboardCategories);
        
        // Expand all categories by default
        const allCategoryIds = dashboardCategories.map(cat => cat.id);
        setExpandedCategories(new Set(allCategoryIds));
        
        // Set default dashboard (first dashboard or first with isDefault flag)
        const allDashboards = dashboardCategories.flatMap(cat => cat.dashboards);
        const defaultDashboard = allDashboards.find(d => d.isDefault) || allDashboards[0];
         console.log('Default dashboard:', defaultDashboard);
         if (defaultDashboard) {
           setSelectedDashboard(defaultDashboard);
           onSelectedDashboardChange?.(defaultDashboard);
         }
        
        // Reset retry count on success
        setRetryCount(0);
      } catch (err) {
        console.error('Failed to load dashboards:', err);
        let errorMessage = 'Failed to load dashboards. Please try again.';
        
        if (err instanceof Error) {
          errorMessage = err.message;
        } else if (typeof err === 'object' && err !== null && 'response' in err) {
          // Handle axios error
          const axiosError = err as any;
          if (axiosError.response?.data?.detail) {
            errorMessage = `API Error: ${axiosError.response.data.detail}`;
          } else if (axiosError.response?.status) {
            errorMessage = `Server Error (${axiosError.response.status}): Failed to load dashboards`;
          }
        }
        
        setError(errorMessage);
      } finally {
        setLoading(false);
      }
    };

    loadDashboards();
  }, [clientId, retryCount]);

  // Retry function
  const handleRetry = () => {
    setRetryCount(prev => prev + 1);
  };

  // Handle responsive behavior and iframe height
  useEffect(() => {
    const handleResize = () => {
      const newIsMobile = window.innerWidth < MOBILE_BREAKPOINT;
      setIsMobile(newIsMobile);
      
      // Update iframe height
      if (iframeRef.current) {
        const viewportHeight = window.innerHeight;
        const newHeight = Math.max(600, viewportHeight - (isMaximized ? 100 : 200));
        iframeRef.current.style.height = `${newHeight}px`;
      }
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [selectedDashboard, isMaximized]);

  // Handle maximized state changes - only auto-collapse when entering maximized/mobile mode
  useEffect(() => {
    // Auto-collapse when entering maximized or mobile mode
    // But allow manual toggle while in those modes
    if (isMaximized || isMobile) {
      setIsDrawerOpen(false);
    } else {
      setIsDrawerOpen(true);
    }
  }, [isMaximized, isMobile]);

  // Handle conversation ID updates from parent
  useEffect(() => {
    if (updatedConversationId && selectedDashboard) {
      console.log('Updating dashboard conversation_id:', updatedConversationId);
      
      // Update the dashboard URL with the new conversation_id
      let updatedUrl = selectedDashboard.url;
      if (updatedUrl) {
        const urlObj = new URL(updatedUrl);
        urlObj.searchParams.set('conversation_id', updatedConversationId);
        updatedUrl = urlObj.toString();
      }
      
      // Update the selected dashboard with new conversation_id and URL
      const updatedDashboard = {
        ...selectedDashboard,
        conversation_id: updatedConversationId,
        url: updatedUrl
      };
      
      setSelectedDashboard(updatedDashboard);
      setIframeLoading(true); // Trigger reload animation
      
      // Also update the dashboard in the categories list so switching back to it uses the new ID
      setCategories((prevCategories) =>
        prevCategories.map((category) => ({
          ...category,
          dashboards: category.dashboards.map((dashboard) =>
            dashboard.Id === selectedDashboard.Id
              ? updatedDashboard
              : dashboard
          ),
        }))
      );
      
      console.log('Dashboard and categories updated with new conversation_id:', updatedConversationId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updatedConversationId]);

  const handleDrawerToggle = () => {
    setIsDrawerOpen(!isDrawerOpen);
  };

  const handleCategoryToggle = (categoryId: string) => {
    const newExpanded = new Set(expandedCategories);
    if (newExpanded.has(categoryId)) {
      newExpanded.delete(categoryId);
    } else {
      newExpanded.add(categoryId);
    }
    setExpandedCategories(newExpanded);
  };

  const handleDashboardSelect = (dashboard: GuardrailApp) => {
    // Don't reload if the same dashboard is already selected
    if (selectedDashboard?.Id === dashboard.Id) {
      return;
    }
    
    // Show loading state
    setDashboardLoading(true);
    
    // Update URL with client context if available
    let dashboardUrl = dashboard.url;
    if (clientData) {
      const url = new URL(dashboardUrl);
      if (clientData.domains) {
        url.searchParams.set('client_domains', clientData.domains);
      }
      if (clientData.name) {
        url.searchParams.set('client_name', clientData.name);
      }
      dashboardUrl = url.toString();
    }
    
    const updatedDashboard = { ...dashboard, url: dashboardUrl };
    setSelectedDashboard(updatedDashboard);
    setIframeLoading(true); // Start iframe loading
    setDashboardLoading(false); // Clear dashboard selection loading immediately
    
    // Notify parent component of dashboard change
    onSelectedDashboardChange?.(updatedDashboard);
  };

  const drawerContent = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <Box
        sx={{
          px: 2,
          borderBottom: 1,
          borderColor: 'divider',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          minHeight: 64
        }}
      >
        {isDrawerOpen && (
          <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
            <DashboardIcon sx={{ mr: 1, color: 'primary.main' }} />
            <Typography variant="h6" noWrap>
              Dashboards
            </Typography>
          </Box>
        )}
        <Tooltip title={isDrawerOpen ? 'Collapse sidebar' : 'Expand sidebar'}>
          <IconButton onClick={handleDrawerToggle} size="small">
            {isDrawerOpen ? <ChevronLeftIcon /> : <ChevronRightIcon />}
          </IconButton>
        </Tooltip>
      </Box>

      {/* Content */}
      <Box sx={{ flex: 1, overflow: 'auto' }}>
        {loading ? (
          <Box sx={{ p: 2, display: 'flex', justifyContent: 'center' }}>
            <CircularProgress size={24} />
          </Box>
        ) : error ? (
          <Box sx={{ p: 2 }}>
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
            {isDrawerOpen && (
              <Button
                variant="outlined"
                size="small"
                onClick={handleRetry}
                fullWidth
                disabled={loading}
              >
                {loading ? 'Retrying...' : 'Retry'}
              </Button>
            )}
          </Box>
        ) : isDrawerOpen ? (
          <List dense sx={{ py: 0 }}>
            {categories.map((category) => (
              <Box key={category.id}>
                {/* Category Dashboards - Always visible */}
                <Box sx={{ p: 1 }}>
                  {category.dashboards.map((dashboard) => {
                    const isSelected = selectedDashboard?.Id === dashboard.Id;
                    
                    return (
                      <Box
                        key={dashboard.Id}
                        onClick={() => handleDashboardSelect(dashboard)}
                        sx={{
                          position: 'relative',
                          width: '100%',
                          height: 120,
                          mb: 2,
                          borderRadius: 2,
                          overflow: 'hidden',
                          cursor: 'pointer',
                          border: isSelected ? 3 : 1,
                          borderColor: isSelected ? 'primary.main' : 'divider',
                          transition: 'all 0.2s ease-in-out',
                          boxShadow: isSelected ? '0 0 0 2px rgba(25, 118, 210, 0.3), 0 4px 12px rgba(25, 118, 210, 0.4)' : '0 1px 3px rgba(0,0,0,0.1)',
                          '&:hover': {
                            borderColor: 'primary.main',
                            transform: 'scale(1.02)',
                            boxShadow: isSelected 
                              ? '0 0 0 2px rgba(25, 118, 210, 0.3), 0 4px 12px rgba(25, 118, 210, 0.4)'
                              : '0 2px 8px rgba(25, 118, 210, 0.3)'
                          }
                        }}
                      >
                        {/* Thumbnail or Fallback */}
                        {dashboard.thumbnailUrl ? (
                          <Box
                            component="img"
                            src={dashboard.thumbnailUrl}
                            alt={dashboard.PublishedTitle}
                            sx={{
                              width: '100%',
                              height: '100%',
                              objectFit: 'cover',
                              backgroundColor: 'grey.100'
                            }}
                            onError={(e) => {
                              // Hide broken image and show fallback
                              e.currentTarget.style.display = 'none';
                              const fallback = e.currentTarget.nextElementSibling as HTMLElement;
                              if (fallback) fallback.style.display = 'flex';
                            }}
                          />
                        ) : null}
                        
                        {/* Fallback when no thumbnail */}
                        <Box
                          sx={{
                            position: dashboard.thumbnailUrl ? 'absolute' : 'relative',
                            top: 0,
                            left: 0,
                            width: '100%',
                            height: '100%',
                            display: dashboard.thumbnailUrl ? 'none' : 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            backgroundColor: 'grey.100',
                            color: 'grey.500'
                          }}
                        >
                          <ChartIcon sx={{ fontSize: 48 }} />
                        </Box>

                        {/* Selected Overlay - adds a subtle blue tint */}
                        {isSelected && (
                          <Box
                            sx={{
                              position: 'absolute',
                              top: 0,
                              left: 0,
                              right: 0,
                              bottom: 0,
                              backgroundColor: 'rgba(25, 118, 210, 0.15)',
                              pointerEvents: 'none'
                            }}
                          />
                        )}

                        {/* Selected Badge - checkmark in top-right corner */}
                        {isSelected && (
                          <Box
                            sx={{
                              position: 'absolute',
                              top: 8,
                              right: 8,
                              backgroundColor: 'primary.main',
                              borderRadius: '50%',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
                              zIndex: 2
                            }}
                          >
                            <CheckCircleIcon 
                              sx={{ 
                                fontSize: 28,
                                color: 'white'
                              }} 
                            />
                          </Box>
                        )}

                        {/* Title Overlay */}
                        <Box
                          sx={{
                            position: 'absolute',
                            bottom: 0,
                            left: 0,
                            right: 0,
                            backgroundColor: isSelected 
                              ? 'rgba(25, 118, 210, 0.9)'
                              : 'rgba(0,0,0,0.75)',
                            color: 'white',
                            p: 1,
                            transition: 'background-color 0.2s ease-in-out'
                          }}
                        >
                          <Typography
                            variant="caption"
                            sx={{
                              fontWeight: 600,
                              lineHeight: 1.2,
                              display: '-webkit-box',
                              WebkitLineClamp: 1,
                              WebkitBoxOrient: 'vertical',
                              overflow: 'hidden'
                            }}
                          >
                            {dashboard.PublishedTitle}
                          </Typography>
                        </Box>
                      </Box>
                    );
                  })}
                </Box>

                {/* Category separator */}
                {category.id !== categories[categories.length - 1]?.id && (
                  <Divider sx={{ my: 1 }} />
                )}
              </Box>
            ))}
          </List>
        ) : (
          <List dense sx={{ py: 1 }}>
            {/* Collapsed state - show dashboard icons */}
            {categories.flatMap(cat => cat.dashboards).map((dashboard) => (
              <ListItem key={dashboard.Id} disablePadding>
                <Tooltip title={dashboard.PublishedTitle} placement="right" arrow>
                  <ListItemButton
                    selected={selectedDashboard?.Id === dashboard.Id}
                    onClick={() => handleDashboardSelect(dashboard)}
                    sx={{
                      minHeight: 48,
                      justifyContent: 'center',
                      px: 2.5,
                    }}
                  >
                    <ChartIcon fontSize="small" />
                  </ListItemButton>
                </Tooltip>
              </ListItem>
            ))}
          </List>
        )}
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', height: '100%', width: '100%' }}>
      {/* Navigation Drawer */}
      <Drawer
        variant={isMobile ? "temporary" : "permanent"}
        open={isMobile ? isDrawerOpen : true}
        onClose={() => setIsDrawerOpen(false)}
        sx={{
          width: isDrawerOpen ? DRAWER_WIDTH : COLLAPSED_DRAWER_WIDTH,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: isDrawerOpen ? DRAWER_WIDTH : COLLAPSED_DRAWER_WIDTH,
            boxSizing: 'border-box',
            position: isMobile ? 'fixed' : 'relative',
            height: '100%',
            transition: 'width 0.2s ease-in-out',
            borderRight: 1,
            borderColor: 'divider',
            zIndex: isMobile ? 1300 : 'auto'
          }
        }}
      >
        {drawerContent}
      </Drawer>

      {/* Main Content Area */}
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          minWidth: 0,
          height: '100%',
          position: 'relative'
        }}
      >
        {selectedDashboard ? (
          <>
            {iframeLoading && (
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: 'rgba(255, 255, 255, 0.9)',
                  zIndex: 1000
                }}
              >
                <CircularProgress size={40} sx={{ mb: 2 }} />
                <Typography variant="body2" color="text.secondary">
                  Loading dashboard...
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                  This may take up to 20 seconds for cold start
                </Typography>
              </Box>
            )}
            <iframe
              ref={iframeRef}
              src={selectedDashboard.url}
              width="100%"
              allow="clipboard-write"
              onLoad={() => setIframeLoading(false)}
              onError={() => setIframeLoading(false)}
              style={{
                ...iframeStyle,
                border: 'none',
                flex: 1,
                minHeight: isMaximized ? 0 : '500px',
                opacity: dashboardLoading ? 0.5 : 1,
                transition: 'opacity 0.3s ease-in-out'
              }}
              title={selectedDashboard.PublishedTitle}
            />
          </>
        ) : (
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexDirection: 'column',
              color: 'text.secondary'
            }}
          >
            <DashboardIcon sx={{ fontSize: 64, mb: 2, opacity: 0.5 }} />
            <Typography variant="h6" gutterBottom>
              Select a Dashboard
            </Typography>
            <Typography variant="body2" textAlign="center">
              Choose a dashboard from the navigation panel to view your data
            </Typography>
          </Box>
        )}
      </Box>

      {/* Mobile Navigation FAB */}
      {isMobile && !isDrawerOpen && (
        <Fab
          color="primary"
          aria-label="open navigation"
          onClick={() => setIsDrawerOpen(true)}
          sx={{
            position: 'fixed',
            bottom: 16,
            left: 16,
            zIndex: 1400
          }}
        >
          <MenuIcon />
        </Fab>
      )}
    </Box>
  );
};

export default DashboardNavigation;
